/* ==========================================================================
   Rook UI — Benefits Stage Controller
   Web Component: <rk-benefits-stage>

   Palco da seção "Por que Muvon". Sequência ao entrar na viewport:
     1. carrega + dá play no vídeo visível (desktop ou mobile, lazy via data-src);
     2. após `data-delay` ms, adiciona `is-revealed` → o CSS faz crossfade do
        vídeo para a imagem fixa e desliza os pillars para a posição final
        (cascata via --rk-pillar-delay).

   Hydration tier: 'eager' (igual rk-reveal) — o estado inicial escondido
   (.is-ready) precisa ser aplicado ANTES da seção entrar na viewport para
   evitar flash. O IntersectionObserver interno dispara o reveal.

   Degrade: sem JS, o stage nunca recebe .is-ready → o CSS mostra a imagem fixa
   e os pillars já na posição final. prefers-reduced-motion: idem (não hidrata o
   estado de animação).
   ========================================================================== */

import { RkComponent } from '@rook/component';

class RkBenefitsStage extends RkComponent {
  static hydration = 'eager';

  #observer = null;
  #timer = null;

  setup() {
    // prefers-reduced-motion: mantém o estado estático (imagem + pillars fixos).
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this._delay = parseInt(this.dataset.delay, 10) || 0;
    this._stagger = parseInt(this.dataset.stagger, 10) || 0;

    this._pillars = Array.from(this.querySelectorAll('.rk-benefits__pillar'));
    this._videos = Array.from(this.querySelectorAll('.rk-benefits__video'));

    // Cascata: cada pillar atrasa pelo seu índice (na ordem do DOM).
    this._pillars.forEach((pillar, i) => {
      pillar.style.setProperty('--rk-pillar-delay', `${this._stagger * i}ms`);
    });

    // Aplica o estado inicial escondido antes do viewport (sem flash).
    this.classList.add('is-ready');

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          this.#observer.disconnect();
          this.#observer = null;
          this.#start();
        }
      },
      { threshold: 0.35 }
    );
    this.#observer.observe(this);
  }

  #start() {
    this.#playVisibleVideo();
    this.#timer = setTimeout(() => {
      this.classList.add('is-revealed');
      this.rkEmit('benefits:revealed');
    }, this._delay);
  }

  // Carrega (lazy) e toca apenas o vídeo atualmente visível (desktop/mobile).
  #playVisibleVideo() {
    this._videos.forEach((video) => {
      if (getComputedStyle(video).display === 'none') return;

      let loaded = false;
      video.querySelectorAll('source[data-src]').forEach((source) => {
        if (!source.getAttribute('src')) {
          source.setAttribute('src', source.dataset.src);
          loaded = true;
        }
      });
      if (loaded) video.load();

      const playback = video.play();
      if (playback && typeof playback.catch === 'function') {
        // Autoplay bloqueado: o crossfade ainda revela a imagem fixa no delay.
        playback.catch(() => {});
      }
    });
  }

  teardown() {
    this.#observer?.disconnect();
    this.#observer = null;
    if (this.#timer) {
      clearTimeout(this.#timer);
      this.#timer = null;
    }
  }
}

if (!customElements.get('rk-benefits-stage')) {
  customElements.define('rk-benefits-stage', RkBenefitsStage);
}
